// DEPENDENCIES
import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import { useFormik } from "formik";
// COMPONENTS
import MUIDataTable from "mui-datatables";
import { Link, useParams } from "react-router-dom";
import { Button, Modal, Form, Tooltip, OverlayTrigger } from "react-bootstrap";
// CUSTOM COMPONENTS
import * as alert from "../../../redux/alertSnackBarRedux";
import * as confirmModal from "../../../redux/confirmModalRedux";
// SERVICES AND HELPERS
import * as trainingSubTypeService from "../../../services/companyData/trainingSubTypeService";
import TrainingSubTypeValidator from "../../../helpers/validators/TrainingTypeValidator";
import { getInputClasses } from "../../../helpers/helper";

const TrainingSubTypeListingPage = (props) => {
    var initialTrainingSubType = {
        id: 0,
        trainingTypeId: credentialId,
        name: '',
        description: ''
    }

    const { showAlert, showConfirmModal, hideConfirmModal, history, setProcessing } = props;
    const { name } = { ...history.location.state };
    const { credentialId } = useParams();
    const [data, setData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [selectedTrainingSubType, setSelectedTrainingSubType] = useState(initialTrainingSubType);

    useEffect(() => {
        loadTableData();
    }, [showArchived])

    const loadTableData = () => {
        trainingSubTypeService.getTrainingSubTypes(credentialId, showArchived).then(res => {
            setData(res);
        }).catch((ex) => {
            showAlert({ message: ex.message, variant: "error" });
        });
    }

    const editTrainingSubType = (id) => {
        trainingSubTypeService.getTrainingSubTypeById(id).then(res => {
            setSelectedTrainingSubType(res);
            setIsModalOpen(true);
        })
    }

    const archiveTrainingSubType = (id) => {
        setProcessing();
        trainingSubTypeService.archiveTrainingSubType(id).then(res => {
            showAlert({ message: res.message, variant: "success" });
            loadTableData();
        }).catch(ex => {
            showAlert({ message: ex.message, variant: "error" });
        }).finally(() => hideConfirmModal())
    }

    const unarchiveTrainingSubType = (id) => {
        setProcessing();
        trainingSubTypeService.unarchiveTrainingSubType(id).then(res => {
            showAlert({ message: res.message, variant: "success" });
            loadTableData();
        }).catch(ex => {
            showAlert({ message: ex.message, variant: "error" });
        }).finally(() => hideConfirmModal())
    }

    const columns = [
        {
            name: "id",
            label: "Actions",
            options: {
                filter: false,
                sort: false,
                width: 1,
                download: false,
                customBodyRenderLite: (dataIndex) => {
                    return (
                        <>
                            {
                                !showArchived &&
                                <button
                                    className="btn btn-sm btn-info font-weight-bold py-2 px-3 px-xxl-5"
                                    onClick={() => editTrainingSubType(data[dataIndex].id)}
                                >
                                    Edit
                                </button>
                            } {' '}
                            {
                                showArchived ?
                                    <button
                                        onClick={() => showConfirmModal({
                                            title: "Unarchive Credential Sub Type",
                                            text: "Are you sure you want to un-archive this credential sub type? <strong>Unarchiving the sub type will not unarchive credentials assigned to this sub type.</strong>",
                                            rightBtnText: "Confirm",
                                            btnAction: () => unarchiveTrainingSubType(data[dataIndex].id),
                                        })}
                                        className="btn btn-sm btn-warning font-weight-bold py-2 px-3 px-xxl-5 my-1"
                                    >
                                        Unarchive
                                    </button> :
                                    <button
                                        onClick={() => showConfirmModal({
                                            title: "Archive Credential Sub Type",
                                            text: "Are you sure you want to archive this credential sub type? <strong>Archiving this sub type will set all the credentials and credentials assigned to users as archived.</strong>",
                                            rightBtnText: "Confirm",
                                            btnAction: () => archiveTrainingSubType(data[dataIndex].id)
                                        })}
                                        className="btn btn-sm btn-warning font-weight-bold py-2 px-3 px-xxl-5 my-1"
                                    >
                                        Archive
                                    </button>
                            }

                        </>
                    );
                }
            },

        },
        {
            name: "name",
            label: "Name",
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: "description",
            label: "Description",
            options: {
                filter: true,
                sort: true,
                customBodyRenderLite: (dataIndex) => {
                    return (
                        <>
                            {data[dataIndex].description ? data[dataIndex].description : "-"}
                        </>
                    );
                }
            }
        }
    ];

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedTrainingSubType(initialTrainingSubType);
        formik.resetForm();
    }

    const saveTrainingSubType = (values, setStatus, setSubmitting) => {
        trainingSubTypeService.saveTrainingSubType({ ...values, credentials: null, trainingType: null, trainingTypeId: credentialId }).then(res => {
            showAlert({ message: res.message, variant: "success" });
            loadTableData();
            handleModalClose();
        }).catch((ex) => {
            showAlert({ message: ex.message, variant: "error" });
        })
    }

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: selectedTrainingSubType,
        validationSchema: TrainingSubTypeValidator,
        onSubmit: (values, { setStatus, setSubmitting }) => {
            saveTrainingSubType(values, setStatus, setSubmitting);
        }
    });

    return (
        <div className="row" >
            <Modal centered size="lg" show={isModalOpen} onHide={handleModalClose}>
                <form className="card card-custom" onSubmit={formik.handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>Credential Sub Type</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="form">
                            <div className="form-group row">
                                <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                                    Name
                                </label>
                                <div className="col-lg-9 col-xl-9">
                                    <div className="input-group input-group-lg input-group-solid">
                                        <input
                                            mask="999-99-9999"
                                            placeholder="Start typing..."
                                            className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "name")}`}
                                            name="name"
                                            {...formik.getFieldProps("name")}
                                        />
                                    </div>
                                    {formik.touched.name && formik.errors.name ? (
                                        <div className="invalid-feedback display-block">
                                            {formik.errors.name}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            <div className="form-group row">
                                <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                                    Description
                                </label>
                                <div className="col-lg-9 col-xl-9">
                                    <div className="input-group input-group-lg input-group-solid">
                                        <Form.Control as="textarea" rows="5"
                                            placeholder="Start typing..."
                                            className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "description")}`}
                                            name="description"
                                            {...formik.getFieldProps("description")}
                                        />
                                    </div>
                                    {formik.touched.description && formik.errors.description ? (
                                        <div className="invalid-feedback display-block">
                                            {formik.errors.description}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleModalClose}>
                            Close
                        </Button>
                        <button
                            type="submit"
                            className="btn btn-success mr-2"
                            disabled={
                                formik.isSubmitting || (formik.touched && !formik.isValid)
                            }
                        >
                            Save
                            {formik.isSubmitting}
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>
            <div className="col-lg-12 col-xxl-12">
                <div className={`card card-custom card-stretch gutter-b}`}>
                    <div className="card-header border-0 py-5">
                        <h3 className="card-title align-items-start flex-column">
                            <span className="card-label font-weight-bolder text-dark archive-unarchive-span">
                                Credential Sub Types: {name}
                                <OverlayTrigger
                                    key={'right'}
                                    placement={'right'}
                                    overlay={
                                        <Tooltip id={`tooltip-right`}>
                                            Switch to <strong> {showArchived ? "active" : "archived"}</strong>
                                        </Tooltip>
                                    }
                                >
                                    <span
                                        onClick={() => setShowArchived(!showArchived)} style={{ cursor: 'pointer' }}
                                        className={`label label-xl label-inline label-light-${!showArchived ? "success" : "warning"}`}
                                    >
                                        {!showArchived ? "Active " : "Archived"}
                                    </span>
                                </OverlayTrigger>
                            </span>
                            <span className="text-muted mt-3 font-weight-bold fz-xs">
                                View and Manage Credential Sub Types for {name}
                            </span>
                        </h3>
                        <div className="card-toolbar">
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="btn btn-success font-weight-bolder font-size-sm mr-3"
                            >
                                Add Sub Type
                            </button>
                            <Link
                                className="btn btn-danger font-weight-bolder font-size-sm mr-3"
                                to={'/company-data/credential-types'}
                            >
                                Cancel
                            </Link>
                        </div>
                    </div>
                    <div className="container-fluid">
                        <MUIDataTable
                            data={data}
                            columns={columns}
                            options={{
                                rowsPerPage: 25,
                                rowsPerPageOptions: [10, 15, 20, 25, 30, 35, 40, 45, 50, 100],
                                selectableRows: 'none'
                            }}
                        />
                    </div>
                </div>
            </div>
        </div >
    )
}

export default connect(null, { ...alert.actions, ...confirmModal.actions })(TrainingSubTypeListingPage);